// @ts-nocheck
import { forwardRef, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  ThemeIcon,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import {
  IconArrowUpRight,
  IconArrowDownRight,
  IconMinus,
} from '@tabler/icons-react';
import { Paper } from '../../../ui';
import { FadeIn, HoverLift } from '../../../animations';
import type { KpiCardProps } from '../types';
import { getContrastingTextColor } from '../../../../utils/color';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * KpiCardWithTrend - KPI card with trend indicator
 *
 * Extends the basic KPI card with trend arrows and percentage change display.
 * Automatically color-codes trends based on whether up/down is positive.
 */
const KpiCardWithTrend = forwardRef<HTMLDivElement, KpiCardProps>(
  (
    {
      title,
      value,
      description,
      icon,
      color = 'blue',
      size = 'md',
      variant = 'default',
      gradientVariant = 'primary',
      trend,
      onClick,
      animate = false,
      animationDuration = 2000,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const gradient = theme.other?.gradients?.[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [displayValue, setDisplayValue] = useState(animate ? 0 : value);

    // Calculate text color for gradient background
    const gradientBg = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    const sizes = {
      xs: {
        padding: 'xs',
        iconSize: 28,
        titleSize: '10px',
        valueSize: 'lg',
        trendSize: '10px',
        trendIconSize: 12,
        gap: 'xs',
      },
      sm: {
        padding: 'sm',
        iconSize: 32,
        titleSize: 'xs',
        valueSize: 'xl',
        trendSize: 'xs',
        trendIconSize: 14,
        gap: 'sm',
      },
      md: {
        padding: 'md',
        iconSize: 40,
        titleSize: 'sm',
        valueSize: '2rem',
        trendSize: 'sm',
        trendIconSize: 16,
        gap: 'md',
      },
      lg: {
        padding: 'lg',
        iconSize: 48,
        titleSize: 'md',
        valueSize: '2.5rem',
        trendSize: 'md',
        trendIconSize: 18,
        gap: 'lg',
      },
      xl: {
        padding: 'xl',
        iconSize: 56,
        titleSize: 'lg',
        valueSize: '3rem',
        trendSize: 'lg',
        trendIconSize: 20,
        gap: 'xl',
      },
    };

    const currentSize = sizes[size];

    // Animated counter
    useEffect(() => {
      if (!animate || typeof value !== 'number') {
        setDisplayValue(value);
        return;
      }

      const startValue = 0;
      const endValue = value;
      const duration = animationDuration;
      const startTime = Date.now();

      const animateValue = () => {
        const now = Date.now();
        const progress = Math.min((now - startTime) / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.floor(startValue + (endValue - startValue) * easeOut);

        setDisplayValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animateValue);
        } else {
          setDisplayValue(endValue);
        }
      };

      requestAnimationFrame(animateValue);
    }, [value, animate, animationDuration]);

    // Determine trend icon and color
    const getTrendIcon = () => {
      if (!trend) return null;
      if (trend.direction === 'up') return IconArrowUpRight;
      if (trend.direction === 'down') return IconArrowDownRight;
      return IconMinus;
    };

    const getTrendColor = () => {
      if (!trend) return 'gray';

      // If isPositive is explicitly set, use that
      if (trend.isPositive !== undefined) {
        return trend.isPositive ? 'teal' : 'red';
      }

      // Default: up is good, down is bad
      if (trend.direction === 'up') return 'teal';
      if (trend.direction === 'down') return 'red';
      return 'gray';
    };

    const TrendIcon = getTrendIcon();
    const trendColor = getTrendColor();

    const paperVariant =
      variant === 'gradient' ? 'gradient' : variant === 'outlined' ? 'outlined' : 'default';

    const isGradient = variant === 'gradient';

    return (
      <FadeIn>
        <HoverLift disabled={!onClick}>
          <Paper
            ref={ref}
            variant={paperVariant}
            gradientVariant={gradientVariant}
            gradientOpacity={isGradient ? 1 : 0.1}
            p={currentSize.padding}
            onClick={onClick}
            className={className}
            style={{
              cursor: onClick ? 'pointer' : 'default',
              height: '100%',
            }}
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={currentSize.gap} style={{ flex: 1 }}>
                <Text
                  size={currentSize.titleSize}
                  c={isGradient ? autoTextColor : 'dimmed'}
                  tt="uppercase"
                  fw={700}
                  style={{ letterSpacing: '0.5px', ...accessibilityStyles }}
                >
                  {title}
                </Text>

                <Group gap="xs" align="baseline">
                  <Text
                    size={currentSize.valueSize}
                    fw={700}
                    c={isGradient ? autoTextColor : undefined}
                    style={{
                      lineHeight: 1,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {displayValue}
                  </Text>

                  {trend && TrendIcon && (
                    <Group
                      gap={4}
                      style={{
                        backgroundColor: isGradient
                          ? 'rgba(255, 255, 255, 0.2)'
                          : theme.colors[trendColor][0],
                        padding: '2px 8px',
                        borderRadius: theme.radius.sm,
                      }}
                    >
                      <TrendIcon
                        size={currentSize.trendIconSize}
                        color={
                          isGradient ? autoTextColor : theme.colors[trendColor][6]
                        }
                      />
                      <Text
                        size={currentSize.trendSize}
                        fw={600}
                        c={
                          isGradient ? autoTextColor : theme.colors[trendColor][7]
                        }
                      >
                        {Math.abs(trend.value)}%
                      </Text>
                    </Group>
                  )}
                </Group>

                {(description || trend?.label) && (
                  <Text
                    size="xs"
                    c={isGradient ? autoTextColor : 'dimmed'}
                    style={{ opacity: isGradient ? 0.9 : 1, ...accessibilityStyles }}
                  >
                    {trend?.label || description}
                  </Text>
                )}
              </Stack>

              {icon && (
                <ThemeIcon
                  size={currentSize.iconSize}
                  radius="md"
                  variant={isGradient ? 'transparent' : 'light'}
                  color={isGradient ? undefined : color}
                  style={{
                    background: isGradient ? 'rgba(255,255,255,0.2)' : undefined,
                    color: isGradient ? autoTextColor : undefined,
                  }}
                >
                  {icon}
                </ThemeIcon>
              )}
            </Group>
          </Paper>
        </HoverLift>
      </FadeIn>
    );
  }
);

KpiCardWithTrend.displayName = 'KpiCardWithTrend';

export default KpiCardWithTrend;
